import { useEffect, useRef, useState } from "react";
import { C, fmtChips } from "../lib/api";
import { presentationsOf } from "../lib/showdown";

const STREET_NAME = { FLOP: "The flop", TURN: "The turn", RIVER: "The river", FOURTH: "Fourth street", FIFTH: "Fifth street", SIXTH: "Sixth street", SEVENTH: "Seventh street, down" };

// Voice line for an action: just the action, no seat or name ("Check", "Bet 300", "Raise to 600").
const spokenAction = (la) => {
  switch (la.action) {
    case "FOLD": return "Fold";
    case "CHECK": return "Check";
    case "CALL": return "Call";
    case "BET": return `Bet ${fmtChips(la.to)}`;
    case "RAISE": return `Raise to ${fmtChips(la.to)}`;
    case "ALL_IN": return `All in ${fmtChips(la.to)}`;
    case "BRING_IN": return `Bring in ${fmtChips(la.to)}`;
    default: return "";
  }
};

const describeAction = (la, name) => {
  switch (la.action) {
    case "FOLD": return `${name} folds${la.auto ? " (clock)" : ""}`;
    case "CHECK": return `${name} checks`;
    case "CALL": return `${name} calls`;
    case "BET": return `${name} bets ${fmtChips(la.to)}`;
    case "RAISE": return `${name} raises to ${fmtChips(la.to)}`;
    case "ALL_IN": return `${name} is ALL IN for ${fmtChips(la.to)}`;
    case "BRING_IN": return `${name} brings it in for ${fmtChips(la.to)}`;
    default: return "";
  }
};

export const voiceEnabled = () => (localStorage.getItem("pfp_dealer_voice") ?? "on") === "on";

// Labels already spell ranks out ("Two pair, Jacks and Fives"); this keeps older single-letter forms speakable and drops the "· bot" tag
const RANK_WORDS = { 2: "two", 3: "three", 4: "four", 5: "five", 6: "six", 7: "seven", 8: "eight", 9: "nine", T: "ten", J: "jack", Q: "queen", K: "king", A: "ace" };
const PLURAL = { six: "sixes" };
export const spokenRank = (label) => label
  .replace(/\b([2-9TJQKA])s\b/g, (_, c) => PLURAL[RANK_WORDS[c]] || `${RANK_WORDS[c]}s`)
  .replace(/\b([2-9TJQKA])\b(?= high| over|$)/g, (_, c) => RANK_WORDS[c])
  .replace(/(to |Straight to |flush to )([2-9TJQKA])\b/g, (_, p, c) => `${p}${RANK_WORDS[c]}`)
  .toLowerCase();
const spokenName = (n) => n.replace(/\s*·\s*bot$/i, "");
// Dealer announces just the rank — "wins with a straight", not "a straight to a ten"
export const shortRank = (label) => {
  const l = label.toLowerCase();
  if (l.endsWith(" low") || l.startsWith("number one") || l.startsWith("wheel")) return spokenRank(label.replace(/\(.*\)/, "").trim()).replace(/-/g, " ");
  if (l.startsWith("royal flush")) return "a royal flush";
  if (l.startsWith("straight flush")) return "a straight flush";
  if (l.startsWith("four ")) return "four of a kind";
  if (l.startsWith("full house")) return "a full house";
  if (l.startsWith("flush")) return "a flush";
  if (l.startsWith("straight")) return "a straight";
  if (l.startsWith("three ")) return "three of a kind";
  if (l.startsWith("two pair")) return "two pair";
  if (l.startsWith("pair")) return "a pair";
  return "high card";
};

// Voice matches the dealer on screen (Celeste = female, Marco = male). Browsers load voices async, so never
// speak with an unresolved voice list — the default is often male, which made Celeste switch voices mid-hand.
const FEMALE = /female|zira|samantha|karen|moira|victoria|fiona|tessa|catherine|serena|google uk english female|google us english|aria|jenny/i;
const MALE = /male|david|daniel|alex|james|lee|rishi|fred|george|ryan|guy|google uk english male/i;
const pickVoice = (voices) => {
  const wantMale = localStorage.getItem("pfp_dealer") === "MODERN";
  const en = voices.filter((v) => v.lang.startsWith("en"));
  const pool = en.length ? en : voices;
  const au = (v) => v.lang === "en-AU";
  const byGender = pool.filter((v) => (wantMale ? MALE.test(v.name) && !/female/i.test(v.name) : FEMALE.test(v.name) && !/\bmale\b/i.test(v.name)));
  return byGender.find(au) || byGender[0] || pool.find(au) || pool[0] || null;
};

function speak(text) {
  if (!voiceEnabled() || !window.speechSynthesis) return;
  const synth = window.speechSynthesis;
  const go = () => {
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text.replace(/\$/g, ""));
    u.voice = pickVoice(synth.getVoices());
    u.rate = 1.02;
    u.pitch = localStorage.getItem("pfp_dealer") === "MODERN" ? 0.95 : 1.1;
    synth.speak(u);
  };
  if (synth.getVoices().length) go();
  else synth.addEventListener("voiceschanged", go, { once: true });
}

export function useDealerLines(state, serverNow = () => Date.now()) {
  const prev = useRef(null);
  const timers = useRef({});
  const dealLands = useRef({ hand_id: null, at: 0 }); // when the last card of this hand's deal reaches the screen
  const [line, setLine] = useState("Welcome. Take a seat.");
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    const p = prev.current;
    prev.current = state;
    if (!state) return;
    if (!p && !state.hand) { setLine(`Welcome to the ${state.table.name.split(" · ")[0]}. Take a seat.`); return; }
    const name = (i) => state.seats[i]?.name || `Seat ${i + 1}`;
    const parts = []; // speech bubble (full commentary)
    const spoken = []; // voice: only actions ("Seat 3 bets 300") and the viewer's own prompt — no names, no recaps
    const h = state.hand;
    let wait = 0; // a fresh deal: hold every line (incl. "$X to call") until the last card has landed on screen
    if (h) {
      const sameHand = p?.hand?.hand_id === h.hand_id;
      if (!sameHand) parts.push(h.bomb ? `Hand ${state.hand_no}. BOMB POT — everyone antes ${fmtChips(h.ante)}, no pre-flop betting, two boards`
        : state.table.family === "STUD" ? `Hand ${state.hand_no}. Everyone antes ${fmtChips(h.ante)} — lowest door card brings it in for ${fmtChips(state.table.bring_in)}`
        : `Hand ${state.hand_no}. ${name(state.bb_seat)} posts the big blind`);
      if (!sameHand && h.bomb) spoken.push("Bomb pot");
      // the hand first arrives while seeds are still being collected (no deck, no clock); the deal only starts once
      // `collecting` clears — the first action deadline includes the deal animation, so that's when the cards have landed
      if (!h.collecting && h.act_deadline && dealLands.current.hand_id !== h.hand_id) {
        dealLands.current = { hand_id: h.hand_id, at: h.act_deadline - h.action_timeout_ms };
      }
      if (dealLands.current.hand_id === h.hand_id) wait = Math.max(0, dealLands.current.at - serverNow());
      const la = h.last_action;
      if (la && JSON.stringify(sameHand ? p.hand.last_action : null) !== JSON.stringify(la)) {
        parts.push(describeAction(la, name(la.seat)));
        spoken.push(spokenAction(la)); // every action, mine included, no seat/name
      }
      if (h.discard && !(sameHand && p.hand.discard)) { parts.push("Everyone throws one card away"); spoken.push("Throw one card away"); }
      if (h.draw && !(sameHand && p.hand.draw)) { parts.push(`Draw ${h.draw.n}: pick the cards to throw away, or stand pat`); if (h.draw.pending.includes(state.my_seat)) spoken.push("Draw, or stand pat"); }
      if (sameHand && p.hand.draw && !h.draw) {
        const tags = state.seats.filter((s) => s.in_hand && !s.folded && s.drew != null).map((s) => `${s.name} ${s.drew === 0 ? "stands pat" : `draws ${s.drew}`}`);
        if (tags.length) parts.push(tags.join(", "));
      }
      if (h.bring_in != null && !(sameHand && p.hand.bring_in != null) && h.to_act === h.bring_in) parts.push(`${name(h.bring_in)} has the bring-in`);
      if (h.uncalled && JSON.stringify(sameHand ? p.hand.uncalled : null) !== JSON.stringify(h.uncalled)) parts.push(`${fmtChips(h.uncalled.amount)} uncalled, back to ${name(h.uncalled.seat)}`);
      if (h.rit_offer && !(sameHand && p.hand.rit_offer)) { parts.push("Run it twice? Both players must agree"); spoken.push("Run it twice?"); }
      if (h.rit && !(sameHand && p.hand.rit)) { parts.push("Running it twice — each board plays for half the pot"); spoken.push("Running it twice"); }
      if (sameHand && p.hand.street !== h.street && STREET_NAME[h.street]) parts.push(h.board.length ? `${STREET_NAME[h.street]}: ${h.board.join(" ")}${h.bomb ? ` · ${h.board2.join(" ")}` : ""}` : STREET_NAME[h.street]);
      if (h.to_act != null && (!sameHand || p.hand.to_act !== h.to_act || parts.length)) {
        const s = state.seats[h.to_act];
        const toCall = Math.min(h.current_bet - s.bet, s.stack);
        const bringIn = h.bring_in === h.to_act && h.current_bet === 0;
        parts.push(bringIn ? `${s.name}, bring it in or complete` : toCall > 0 ? `${s.name}, ${fmtChips(toCall)} to call` : `${s.name}, check or bet`);
        if (h.to_act === state.my_seat && (!sameHand || p.hand.to_act !== h.to_act)) spoken.push(bringIn ? "Bring in or complete" : toCall > 0 ? `${fmtChips(toCall)} to call` : "Check or bet");
      }
    } else if (state.last_result && p?.hand) {
      // showdown: one line per pot, spoken as each is presented on the felt (MAIN → SP1 → SP2 …), in step with PokerTable
      const r = state.last_result;
      const showdown = r.reason === "SHOWDOWN";
      const sidePots = new Set(r.awards.map((a) => a.pot ?? 0)).size > 1;
      const lineFor = (a) => {
        const potName = sidePots ? ((a.pot ?? 0) === 0 ? "the Main pot" : `Side Pot ${a.pot}`) : "";
        const where = a.board ? ` the ${a.board.toLowerCase()}${a.board === "HIGH" || a.board === "LOW" ? "" : " board"}${potName ? ` of ${potName}` : ""}` : potName ? ` ${potName}` : "";
        const who = a.winners.map(name).join(" and ");
        return {
          text: `${who} ${a.winners.length > 1 ? "split" : a.board ? "takes" : "wins"}${where} ${fmtChips(a.amount)}${a.category ? ` with ${shortRank(a.category)}` : ""}`,
          voice: a.winners.length > 1 ? `${who} split${where || " the pot"}` : a.category && showdown ? `${who} wins${where} with ${shortRank(a.category)}` : `${who} wins${where}`,
        };
      };
      const groups = showdown ? presentationsOf(r.awards) : [r.awards];
      if (r.uncalled && JSON.stringify(p.hand.uncalled) !== JSON.stringify(r.uncalled)) parts.push(`${fmtChips(r.uncalled.amount)} uncalled, back to ${name(r.uncalled.seat)}`);
      const bombNext = state.bomb_next ? { text: "Three of a suit on the flop — the next hand is a BOMB POT", voice: "Next hand, bomb pot" } : null;
      wait = showdown ? C.TDA.SHOWDOWN_REVEAL_MS + C.TDA.SHOWDOWN_HOLD_MS : 0; // the banner appears once the cards have turned
      timers.current.seq?.forEach(clearTimeout);
      timers.current.seq = groups.slice(1).map((g, i) => setTimeout(() => { const ls = [...g.map(lineFor), ...(i === groups.length - 2 && bombNext ? [bombNext] : [])]; say(ls.map((l) => l.text).join(". "), ls.map((l) => l.voice).join(". ")); }, wait + (i + 1) * (C.TDA.AWARD_SHOW_MS + C.TDA.AWARD_GAP_MS)));
      for (const l of [...groups[0].map(lineFor), ...(groups.length === 1 && bombNext ? [bombNext] : [])]) { parts.push(l.text); spoken.push(l.voice); }
    } else if (!state.last_result && state.seats.filter((s) => s.user_id).length < 2 && p?.seats && state.seats.filter((s) => s.user_id).length !== p.seats.filter((s) => s.user_id).length) {
      parts.push("Waiting for one more player");
    }
    if (!parts.length) return;
    clearTimeout(timers.current.line); clearTimeout(timers.current.done);
    if (h) timers.current.seq?.forEach(clearTimeout); // a new hand cuts any pot announcements still queued
    timers.current.line = setTimeout(() => say(parts.join(". "), spoken.join(". ")), wait);
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps
  function say(text, voice) {
    setLine(text);
    if (voice) speak(voice);
    setSpeaking(true);
    clearTimeout(timers.current.done);
    timers.current.done = setTimeout(() => setSpeaking(false), Math.min(4000, 900 + text.length * 45));
  }
  useEffect(() => () => { clearTimeout(timers.current.line); clearTimeout(timers.current.done); timers.current.seq?.forEach(clearTimeout); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { line, speaking };
}
